// Lobby controls for the game modes: the "Extra masti" settings and team picking.
import { useEffect, useState } from 'react';
import { DEFAULT_TEAM_NAMES, SETTING_LIMITS, TEAM_PRESETS } from '@shared/rules';
import type { RoomSettings } from '@shared/types';
import { Avatar } from '../../components/Chrome';
import { usePrefs } from '../../components/Prefs';
import type { RoomApi } from '../../lib/useRoom';

function useUpdate(room: RoomApi) {
  const { toast } = usePrefs();
  return async (patch: Partial<RoomSettings>) => {
    const err = await room.act('settings:update', patch);
    if (err) toast(err, 'error');
  };
}

function Toggle({
  label,
  hint,
  on,
  disabled,
  onChange,
}: {
  label: string;
  hint: string;
  on: boolean;
  disabled?: boolean;
  onChange: (on: boolean) => void;
}) {
  return (
    <button type="button" className="mode-toggle" role="switch" aria-checked={on} disabled={disabled} onClick={() => onChange(!on)}>
      <span className="grow">
        <strong className="small">{label}</strong>
        <span className="muted small" style={{ display: 'block' }}>
          {hint}
        </span>
      </span>
      <span className="switch" aria-hidden="true" />
    </button>
  );
}

const ordinal = (n: number) => `${n}${n === 1 ? 'st' : n === 2 ? 'nd' : n === 3 ? 'rd' : 'th'}`;

function Every({
  label,
  hint,
  value,
  options,
  disabled,
  onChange,
}: {
  label: string;
  hint: string;
  value: number;
  options: readonly number[];
  disabled?: boolean;
  onChange: (v: number) => void;
}) {
  return (
    <div className="stack" style={{ gap: 6 }}>
      <div>
        <strong className="small">{label}</strong>
        <span className="muted small" style={{ display: 'block' }}>
          {hint}
        </span>
      </div>
      <div className="segmented" role="group" aria-label={label}>
        {options.map((n) => (
          <button key={n} type="button" aria-pressed={n === value} disabled={disabled && n !== value} onClick={() => !disabled && onChange(n)}>
            {n === 0 ? 'Off' : `Every ${ordinal(n)}`}
          </button>
        ))}
      </div>
    </div>
  );
}

/** "Extra masti": prompt choice, guess who, write-your-own, Sabse Bekaar and teams. */
export function ModeSettings({ room }: { room: RoomApi }) {
  const view = room.view!;
  const s = view.settings;
  const isHost = view.me?.id === view.hostId;
  const update = useUpdate(room);
  const teams = s.teams ?? [];
  const voting = s.judgeMode === 'vote';

  return (
    <section className="panel stack">
      <div className="row between">
        <h2 style={{ margin: 0 }}>Extra masti</h2>
        {!isHost && <span className="chip">host picks</span>}
      </div>
      <Toggle
        label="🃏 Prompt choice"
        hint={voting ? 'Judge mode only. With everyone voting, prompts are dealt.' : 'The judge sees two prompts and picks one.'}
        on={Boolean(s.promptChoice)}
        disabled={!isHost}
        onChange={(on) => update({ promptChoice: on })}
      />
      <Toggle
        label="🕵️ Guess who"
        hint="After each win, everyone guesses who played it. Right guess, +1."
        on={Boolean(s.guessWho)}
        disabled={!isHost}
        onChange={(on) => update({ guessWho: on })}
      />
      <Every
        label="✍️ Write your own prompt"
        hint={voting ? 'Players take turns writing the prompt.' : 'The judge writes the prompt.'}
        value={s.writeOwnEvery ?? 0}
        options={SETTING_LIMITS.writeOwnEvery}
        disabled={!isHost}
        onChange={(v) => update({ writeOwnEvery: v })}
      />
      <Every
        label="🥴 Sabse Bekaar rounds"
        hint="The WORST answer wins the point."
        value={s.worstEvery ?? 0}
        options={SETTING_LIMITS.worstEvery}
        disabled={!isHost}
        onChange={(v) => update({ worstEvery: v })}
      />
      <Toggle
        label="👨‍👩‍👧 Teams"
        hint="Kids vs Parents, or any split you like. Points go to the team."
        on={teams.length > 0}
        disabled={!isHost}
        onChange={(on) => update({ teams: on ? DEFAULT_TEAM_NAMES.slice(0, 2) : [] })}
      />
      {teams.length > 0 && isHost && <TeamNames room={room} teams={teams} />}
    </section>
  );
}

/** Host-only editor for how many teams there are and what they are called. */
function TeamNames({ room, teams }: { room: RoomApi; teams: string[] }) {
  const update = useUpdate(room);
  const [draft, setDraft] = useState(teams);
  const { min, max, nameMax } = SETTING_LIMITS.teams;
  const key = JSON.stringify(teams);

  // Follow the server when the names change (another tab, a preset, or sanitising).
  useEffect(() => setDraft(JSON.parse(key) as string[]), [key]);

  function setCount(n: number) {
    const next = Array.from({ length: n }, (_, i) => teams[i] ?? DEFAULT_TEAM_NAMES[i] ?? `Team ${i + 1}`);
    void update({ teams: next });
  }

  return (
    <div className="stack team-editor" style={{ gap: 8 }}>
      <div className="segmented" role="group" aria-label="How many teams">
        {Array.from({ length: max - min + 1 }, (_, i) => min + i).map((n) => (
          <button key={n} type="button" aria-pressed={teams.length === n} onClick={() => setCount(n)}>
            {n} teams
          </button>
        ))}
      </div>
      <div className="grid-2">
        {draft.map((name, i) => (
          <input
            key={i}
            className={`input team-input team-${i}`}
            value={name}
            maxLength={nameMax}
            aria-label={`Team ${i + 1} name`}
            onChange={(e) => setDraft((d) => d.map((x, j) => (j === i ? e.target.value : x)))}
            onBlur={() => {
              if (JSON.stringify(draft) !== key) void update({ teams: draft });
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            }}
          />
        ))}
      </div>
      <span className="muted small">Quick picks</span>
      <div className="row" style={{ gap: 6 }}>
        {TEAM_PRESETS.map((preset) => (
          <button key={preset.join()} type="button" className="btn btn-sm btn-outline" onClick={() => update({ teams: preset })}>
            {preset.join(' vs ')}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Team picker shown in the lobby when teams are on. */
export function TeamsPanel({ room }: { room: RoomApi }) {
  const view = room.view!;
  const { toast } = usePrefs();
  const me = view.players.find((p) => p.id === view.me?.id);
  if (!view.teams.length) return null;

  async function join(team: number) {
    const err = await room.act('player:team', { team });
    if (err) toast(err, 'error');
  }

  return (
    <section className="panel stack">
      <div className="row between">
        <h2 style={{ margin: 0 }}>Teams</h2>
        <span className="muted small">Tap a team to join it</span>
      </div>
      <div className="team-grid">
        {view.teams.map((t) => {
          const mine = me?.team === t.index;
          return (
            <div key={t.index} className={`team-card team-${t.index} ${mine ? 'mine' : ''}`}>
              <div className="row between" style={{ flexWrap: 'nowrap' }}>
                <strong className="team-name">{t.name}</strong>
                <span className="muted small">{t.playerIds.length}</span>
              </div>
              <ul className="team-members">
                {t.playerIds.map((id) => {
                  const p = view.players.find((x) => x.id === id);
                  return p ? (
                    <li key={id}>
                      <Avatar player={p} /> {p.name}
                      {p.id === me?.id && <span className="muted"> (you)</span>}
                    </li>
                  ) : null;
                })}
                {!t.playerIds.length && <li className="muted small">Nobody yet</li>}
              </ul>
              {!mine && (
                <button type="button" className="btn btn-sm btn-outline btn-block" onClick={() => join(t.index)}>
                  Join {t.name}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

/** Extra host options in a player's sheet: move them to another team. */
export function MoveToTeam({ room, playerId, onDone }: { room: RoomApi; playerId: string; onDone: () => void }) {
  const view = room.view!;
  const { toast } = usePrefs();
  const player = view.players.find((p) => p.id === playerId);
  if (!view.teams.length || !player) return null;
  return (
    <>
      {view.teams
        .filter((t) => t.index !== player.team)
        .map((t) => (
          <button
            key={t.index}
            type="button"
            onClick={async () => {
              const err = await room.act('player:team', { team: t.index, playerId });
              if (err) toast(err, 'error');
              onDone();
            }}
          >
            👥 Move {player.name} to {t.name}
          </button>
        ))}
    </>
  );
}
